/**
 * Fetch-based API Client
 *
 * Lightweight wrapper around native fetch, configured for the Crucio API backend.
 * Maintains the same interface as the previous Axios-based client so consumers
 * can use `response.data` without changes.
 *
 * Why fetch over Axios:
 * - Native to all modern browsers and Node 18+ (no bundle cost)
 * - Axios added ~13KB gzipped for features we didn't use (interceptors were no-ops)
 * - TanStack Query already handles retries, caching, and error state
 *
 * Key difference from raw fetch:
 * - Throws on non-2xx responses (like Axios), so TanStack Query's onError works
 * - Returns { data: T } shape to match existing consumer expectations
 * - Auto-detects FormData and skips Content-Type header (lets browser set boundary)
 */

export const API_BASE_URL =
  import.meta.env.VITE_API_URL || "http://localhost:8000";
const BASE_URL = `${API_BASE_URL}/api/v1`;
// 30s timeout — Guardrails cold start on Cloud Run takes ~45s (ML model loading).
// 10s was too short, causing "signal timed out" on first note submission.
const DEFAULT_TIMEOUT = 30000;

// ─── Bearer Token for Keycloak OIDC (D-029) ───────────────────────────────
// Module-level token storage. AuthProvider calls setAuthToken() whenever the
// OIDC access token changes (login, refresh, logout). The request() function
// reads this to attach Authorization headers automatically.
//
// WHY module-level instead of context: The API client is a plain module, not a
// React component — it can't use hooks. Module-level state is the standard
// pattern for non-React singletons that need runtime configuration.
// (Same approach as Axios interceptors, but without the Axios dependency.)
let authToken: string | null = null;

/**
 * Set the Bearer token for all subsequent API requests.
 * Called by AuthProvider when the OIDC token changes.
 * Pass null on logout to clear the token.
 */
export function setAuthToken(token: string | null): void {
  authToken = token;
}

// ─── 401 Token Refresh (TF.5.3, issue #91) ────────────────────────────────
// Module-level callback for refreshing an expired token. AuthProvider wires
// this up with a closure that calls oidc-client-ts's signinSilent().
//
// WHY this pattern: automaticSilentRenew is proactive (renews BEFORE expiry),
// but race conditions exist — a request can hit the API with a token that
// expired between the renewal check and the fetch. This reactive path catches
// those 401s and retries once with a fresh token.
//
// The pattern mirrors setAuthToken: AuthProvider pushes a function reference
// into this module so the plain-module API client can trigger OIDC operations
// without importing React or oidc-client-ts.
let tokenRefresher: (() => Promise<string | null>) | null = null;

/**
 * Register a callback that attempts to refresh the OIDC access token.
 * Called once by AuthProvider on mount. The callback should call
 * signinSilent() and return the new access_token, or null on failure.
 */
export function setTokenRefresher(
  refresher: (() => Promise<string | null>) | null,
): void {
  tokenRefresher = refresher;
}

/**
 * Response shape matching the previous Axios interface.
 * Consumers access response.data — this preserves that contract.
 */
interface ApiResponse<T> {
  data: T;
  status: number;
}

/** Per-request configuration options. */
interface RequestConfig {
  params?: Record<string, string | number | boolean | undefined>;
  headers?: Record<string, string>;
  timeout?: number;
}

/**
 * Build a full URL with query parameters appended.
 *
 * Filters out undefined values so callers can pass optional params
 * without pre-filtering (e.g., { page: maybePage } where maybePage
 * could be undefined).
 */
function buildUrl(
  path: string,
  params?: Record<string, string | number | boolean | undefined>,
): string {
  const url = new URL(`${BASE_URL}${path}`);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url.toString();
}

/**
 * Build fetch options for a request (extracted to enable retry).
 */
function buildFetchOptions(
  method: string,
  body: unknown | undefined,
  config: RequestConfig,
  timeout: number,
): RequestInit {
  const isFormData = body instanceof FormData;
  const headers: Record<string, string> = {
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    // Attach Bearer token if available (set by AuthProvider via setAuthToken).
    // The API validates this JWT against Keycloak's JWKS public keys.
    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    // Per-request headers override defaults (including Authorization).
    ...config.headers,
  };

  // If caller passes Content-Type for FormData, strip it —
  // browser must set the multipart boundary automatically
  if (isFormData && headers["Content-Type"]) {
    delete headers["Content-Type"];
  }

  return {
    method,
    headers,
    body: body
      ? isFormData
        ? (body as FormData)
        : JSON.stringify(body)
      : undefined,
    signal: AbortSignal.timeout(timeout),
  };
}

/**
 * Core fetch wrapper that all HTTP methods delegate to.
 *
 * Throws on non-2xx status codes (matching Axios behavior) so that
 * TanStack Query's error handling and onError callbacks work unchanged.
 *
 * 401 RETRY (TF.5.3, issue #91):
 * If the API returns 401 and a tokenRefresher is registered, attempts to
 * silently refresh the OIDC token and retries the request ONCE. This covers
 * race conditions where the token expires between the proactive renewal
 * check (automaticSilentRenew) and the actual fetch.
 *
 * Retry happens at most once to prevent infinite loops (e.g., if the
 * refresh token itself is expired or Keycloak is down).
 */
async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  config: RequestConfig = {},
): Promise<ApiResponse<T>> {
  const url = buildUrl(path, config.params);
  const timeout = config.timeout ?? DEFAULT_TIMEOUT;

  const response = await fetch(
    url,
    buildFetchOptions(method, body, config, timeout),
  );

  // ─── 401 Retry with Token Refresh ─────────────────────────────────
  // If we get a 401 and have a refresher, try to get a fresh token and
  // retry once. This handles the race condition where:
  //   t=0:   automaticSilentRenew checks → token still valid (2s left)
  //   t=1:   request starts
  //   t=2:   token expires
  //   t=2.5: API validates token → 401
  //
  // Without this, the user sees an error until the next proactive renewal
  // fires (up to accessTokenExpiringNotificationTimeInSeconds later).
  if (response.status === 401 && tokenRefresher) {
    // Wrap in try/catch so a throwing refresher (e.g., signinSilent rejects
    // unexpectedly) falls through to the original 401 instead of surfacing
    // an unrelated error to callers who expect error.status === 401.
    try {
      const newToken = await tokenRefresher();
      if (newToken) {
        // Token refreshed — update via public setter for consistency.
        // Direct assignment would bypass any future side effects (logging,
        // metrics) added to setAuthToken.
        setAuthToken(newToken);
        const retryResponse = await fetch(
          url,
          buildFetchOptions(method, body, config, timeout),
        );
        return handleResponse<T>(retryResponse, method, path);
      }
    } catch {
      // Refresh threw — treat as failed refresh, fall through to original 401.
    }
    // Refresh failed (session expired, Keycloak unreachable) — fall through
    // to throw the original 401 so the UI can redirect to login.
  }

  return handleResponse<T>(response, method, path);
}

/**
 * Process a fetch Response into our ApiResponse shape, or throw on error.
 */
async function handleResponse<T>(
  response: Response,
  method: string,
  path: string,
): Promise<ApiResponse<T>> {
  if (!response.ok) {
    const error = new Error(
      `Request failed: ${method} ${path} (${response.status})`,
    );
    (error as Error & { status: number }).status = response.status;
    throw error;
  }

  // Handle 204 No Content (e.g., DELETE responses)
  if (response.status === 204) {
    return { data: undefined as T, status: response.status };
  }

  const data: T = await response.json();
  return { data, status: response.status };
}

/**
 * API client with typed HTTP methods.
 *
 * Usage matches the previous Axios instance:
 *   const response = await api.get<MyType>('/path');
 *   return response.data;
 */
export const api = {
  get<T>(path: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    return request<T>("GET", path, undefined, config);
  },

  post<T>(
    path: string,
    data?: unknown,
    config?: RequestConfig,
  ): Promise<ApiResponse<T>> {
    return request<T>("POST", path, data, config);
  },

  put<T>(
    path: string,
    data?: unknown,
    config?: RequestConfig,
  ): Promise<ApiResponse<T>> {
    return request<T>("PUT", path, data, config);
  },

  patch<T>(
    path: string,
    data?: unknown,
    config?: RequestConfig,
  ): Promise<ApiResponse<T>> {
    return request<T>("PATCH", path, data, config);
  },

  delete<T>(path: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    return request<T>("DELETE", path, undefined, config);
  },
};

export default api;
